import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Loader2, AlertCircle } from 'lucide-react';

const BOT_NAME = 'Dr. Floss'; // our friendly (definitely-not-a-real-dentist) AI assistant

const getTimeBasedGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
};

const getInitialMessage = () =>
  `${getTimeBasedGreeting()}! I'm ${BOT_NAME}, your Pristine Smile virtual assistant. How can I help you today?`;

const PristineSmileChatBot = () => {
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [messages, setMessages] = useState([
    { type: 'bot', text: getInitialMessage() }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const chatInputRef = useRef(null);

  // ============ CONFIGURATION ============
  // Replace with your actual backend API endpoint
  // For development with proxy, use relative URL: '/ask/'
  // For production, use full URL: 'http://your-domain.com/ask/'
  const API_ENDPOINT = 'https://pristine-smile-chatbot.onrender.com/ask';
  // const API_ENDPOINT = 'http://0.0.0.0:8000/ask/'; // Use this for development with proxy
  
  // Optional: Add authentication headers if needed
  const getHeaders = () => ({
    'Content-Type': 'application/json',
    // 'Authorization': 'Bearer YOUR_TOKEN', // Uncomment if needed
  });
  
  // Optional: Customize the request body structure
  const buildRequestBody = (userMessage) => ({
    question: userMessage,
    // Add any other parameters your API needs
    // timestamp: new Date().toISOString(),
    // sessionId: 'some-session-id',
  });
  
  // Optional: Parse the API response based on your backend structure
  const parseApiResponse = (data) => {
    console.log(data);
    return data.answer;
    // Adjust this based on your API response structure
    // return data.response || data.message || data.answer || 'I received your message!';
  };
  // =======================================

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isChatOpen && chatInputRef.current) {
      chatInputRef.current.focus();
    }
  }, [isChatOpen]);

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');
    
    // Add user message to chat
    setMessages(prev => [...prev, { type: 'user', text: userMessage }]);
    setIsLoading(true);

    try {
      const response = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(buildRequestBody(userMessage)),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const botResponse = parseApiResponse(data);
      console.log('Bot response:', botResponse);
      console.log('data', data)
      
      // Add bot response
      setMessages(prev => [...prev, { 
        type: 'bot', 
        text: botResponse
      }]);
    } catch (error) {
      console.error('Error calling chatbot API:', error);
      
      let errorMessage = 'Sorry, I encountered an error. ';
      
      if (error.message.includes('Failed to fetch') || error.message.includes('Network')) {
        errorMessage += 'Unable to connect to the server. Please check if the backend is running on http://127.0.0.1:8000 or enable CORS.';
      } else if (error.message.includes('CORS')) {
        errorMessage += 'CORS error detected. Please configure CORS headers on your backend server.';
      } else {
        errorMessage += 'Please try again later or contact us at reachout@pristinesmile.in';
      }
      
      setMessages(prev => [...prev, { 
        type: 'bot', 
        text: errorMessage
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleCloseChat = () => {
    setIsChatOpen(false);
  };

  const handleOpenChat = () => {
    setIsChatOpen(true);
  };

  return (
    <>
      {/* Fixed Positioning Container */}
      <div className="fixed bottom-6 right-6 z-50">
        {/* Chat Window */}
        {isChatOpen && (
          <div className="mb-4 w-96 max-w-[calc(100vw-3rem)] bg-white rounded-2xl shadow-2xl overflow-hidden animate-slideUp">
            {/* Chat Header */}
            <div className="bg-gradient-to-r from-teal-500 to-lime-500 p-4 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-md">
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" className="text-teal-600"/>
                  </svg>
                </div>
                <div>
                  <h3 className="font-bold text-white">{BOT_NAME} · Pristine Smile Assistant</h3>
                  <p className="text-xs text-white/90 flex items-center">
                    <span className="inline-block w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse"></span>
                    Online
                  </p>
                </div>
              </div>
              <button 
                onClick={handleCloseChat}
                className="text-white hover:bg-white/20 rounded-full p-2 transition-all duration-200"
                aria-label="Close chat"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Disclaimer Banner */}
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '8px',
                backgroundColor: '#FFFBEB',
                borderBottom: '1px solid #FDE68A',
                padding: '8px 16px',
              }}
            >
              <AlertCircle
                style={{ width: 16, height: 16, color: '#F59E0B', marginTop: 2, flexShrink: 0 }}
              />
              <p style={{ fontSize: 11, lineHeight: 1.4, color: '#92400E', margin: 0 }}>
                {BOT_NAME} is an AI assistant and can make mistakes. For diagnosis or treatment advice, please consult our dentists directly.
              </p>
            </div>

            {/* Chat Messages */}
            <div className="h-96 overflow-y-auto p-4 space-y-4 bg-gradient-to-b from-gray-50 to-white">
              {messages.map((msg, idx) => (
                <div 
                  key={idx}
                  className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
                  style={{
                    animation: 'fadeIn 0.3s ease-out'
                  }}
                >
                  {msg.type === 'bot' && (
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-teal-500 to-lime-500 flex items-center justify-center text-white font-bold mr-2 flex-shrink-0">
                      PS
                    </div>
                  )}
                  <div 
                    className={`max-w-[75%] p-3 rounded-2xl ${
                      msg.type === 'user' 
                        ? 'bg-gradient-to-r from-teal-500 to-teal-600 text-white shadow-md' 
                        : 'bg-white text-gray-800 shadow-md border border-gray-100'
                    }`}
                    style={{
                      borderBottomRightRadius: msg.type === 'user' ? '0.25rem' : '1rem',
                      borderBottomLeftRadius: msg.type === 'bot' ? '0.25rem' : '1rem'
                    }}
                  >
                    <p className="text-sm leading-relaxed">{msg.text}</p>
                  </div>
                </div>
              ))}
              
              {isLoading && (
                <div className="flex justify-start" style={{ animation: 'fadeIn 0.3s ease-out' }}>
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-teal-500 to-lime-500 flex items-center justify-center text-white font-bold mr-2">
                    PS
                  </div>
                  <div className="bg-white p-3 rounded-2xl shadow-md border border-gray-100" style={{ borderBottomLeftRadius: '0.25rem' }}>
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-teal-500 rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-teal-500 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                      <div className="w-2 h-2 bg-teal-500 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                    </div>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>

            {/* Chat Input */}
            <div className="p-4 border-t bg-white">
              <div className="flex space-x-2">
                <input
                  ref={chatInputRef}
                  type="text"
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Type your message..."
                  disabled={isLoading}
                  className="flex-1 px-4 py-3 rounded-full border-2 border-gray-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-400 focus:outline-none transition-all disabled:opacity-50 text-sm"
                  aria-label="Chat message input"
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!inputMessage.trim() || isLoading}
                  className="bg-gradient-to-r from-teal-500 to-teal-600 text-white p-3 rounded-full hover:from-teal-600 hover:to-teal-700 disabled:opacity-50 transition-all transform hover:scale-105 shadow-lg"
                  aria-label="Send message"
                >
                  <Send className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Floating Chat Button */}
        {!isChatOpen && (
          <button
            onClick={handleOpenChat}
            className="bg-gradient-to-r from-teal-500 to-lime-500 text-white p-4 rounded-full shadow-lg hover:shadow-2xl transition-all transform hover:scale-110 flex items-center justify-center relative"
            aria-label="Open chat"
          >
            <MessageCircle className="w-6 h-6" />
            
            {/* Notification Badge (optional - remove if not needed) */}
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center animate-pulse">
              !
            </span>
            
            {/* Ripple Effect */}
            <span className="absolute inset-0 rounded-full bg-teal-400 opacity-75 animate-ping"></span>
          </button>
        )}
      </div>

      {/* Custom Animations */}
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        
        .animate-slideUp {
          animation: slideUp 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
      `}</style>
    </>
  );
};

export default PristineSmileChatBot;